import { Rectangle } from 'pixi.js';
import {
    DynamicInferredDimensions,
    DynamicLevel,
    TextAlignment
} from '../../../enums';
import type {
    CanvasTextMetrics
} from '../metrics/CanvasTextMetrics';
import {
    buildMetricsForText
} from '../metrics/CanvasTextMetrics';


import {
    DEFAULT_SCALE,
    DEFAULT_TEXT_ALPHA,
    getTotalBounds
} from '../textMetricsHelpers';
import { CanvasRenderer } from '../../canvasRenderer/renderer/CanvasRenderer';
import type {
    AnimatableTextProperties,
    CanvasRendererSettings
} from '../../canvasRenderer/renderer/CanvasRenderer';
import type { CanvasTextMetricItemAPI } from '../metrics/CanvasWordElementMetrics';
import type { AnimationConfig } from '../../../libs/AnimationCurveManager';
import type { CanvasTextStyleRendererSettings } from './CanvasTextStyle';
import { CanvasTextStyleRenderer } from './CanvasTextStyle';
import type { CanvasTextDynamicGraphicRenderer } from './CanvasTextDynamicGraphic';

export interface CanvasTextRendererSettings extends CanvasRendererSettings {
    text: string;
    alignment: TextAlignment;
    highlightAreas: [number, number][];
    wordWrap: boolean;
    normalStyle: CanvasTextStyleRendererSettings;
    highlightStyle: CanvasTextStyleRendererSettings;
}

/**
 * CanvasText is a class that renders a block of text.
 */
export class CanvasTextRenderer extends CanvasRenderer<CanvasTextRendererSettings> {
    text = '';
    highlightAreas: [number, number][] = [];
    #textMetrics: CanvasTextMetrics = null;

    wordWrap = false;
    alignment = TextAlignment.LEFT;
    #normalStyle: CanvasTextStyleRenderer = new CanvasTextStyleRenderer();
    #highlightStyle: CanvasTextStyleRenderer = new CanvasTextStyleRenderer();
    #textWithGraphicsBounds: Rectangle = null;

    constructor() {
        super();
        // By default, render highlights as part of the first render pass
        this.#normalStyle.renderHighlighted = true;
        this.#normalStyle.renderHighlightedColor = true;
        this.#highlightStyle.renderHighlighted = false;
    }

    override get hasText(): boolean {
        return this.text.length > 0;
    }

    override get canRender(): boolean {
        return !!this.#textMetrics;
    }

    get normalStyle(): CanvasTextStyleRenderer {
        return this.#normalStyle;
    }

    get highlightStyle(): CanvasTextStyleRenderer {
        return this.#highlightStyle;
    }

    get _styles() {
        return {
            normalStyle: this.normalStyle,
            highlightStyle: this.highlightStyle,
        };
    }

    /** The text metrics for the text block. */
    get metrics(): CanvasTextMetrics {
        return this.#textMetrics;
    }

    get activeTextBounds(): Rectangle {
        return this.#textMetrics.bounds;
    }

    get totalBounds(): Rectangle {
        return this.#textWithGraphicsBounds;
    }

    override apply(settings: CanvasTextRendererSettings) {
        super.apply(settings);

        this.text = settings.text;
        this.highlightAreas = settings.highlightAreas;
        this.wordWrap = settings.wordWrap;
        this.alignment = settings.alignment;

        this.#normalStyle.apply(settings.normalStyle);
        this.#highlightStyle.apply(settings.highlightStyle);
    }

    build() {
        this.resetBuild();

        if (this.text.length === 0) {
            return;
        }

        this.#textMetrics = buildMetricsForText({
            text: this.text,
            alignment: this.alignment,
            normalStyle: this.normalStyle,
            highlightStyle: this.highlightStyle,
            highlightAreas: this.highlightAreas,
            wordWrap: this.wordWrap,
            wordWrapWidth: this.maxTextWidth,
            maxHeight: this.maxTextHeight,
        });

        if (this.#textMetrics.bounds.width === 0 || this.#textMetrics.bounds.height === 0) {
            // TextMetrics has zero width or height. It's possible for empty text assets.
            this.#textWithGraphicsBounds = Rectangle.EMPTY;
        }

        // build the styles and store the bounds of the added graphics
        const bounds: Rectangle[] = [this.#textMetrics.bounds];
        const stylesByBuildOrder = [this.normalStyle, this.highlightStyle];
        stylesByBuildOrder.forEach(style => {
            style.graphics.forEach(graphic => {
                graphic.build(this.#textMetrics);
                if (!graphic.asMask) bounds.push(graphic.bounds);
            });
        });

        // calculate the total bounds of the graphics
        this.#textWithGraphicsBounds = getTotalBounds(bounds);

        this.#initializeAnimationValues();
    }

    /* @inheritDoc */
    override _onSetCurrentTime() {
        // to be implemented once we start implementing animation
        // it's also possible that this might become a NOOP in this class
    }

    #initializeAnimationValues() {
        Object.values(this._styles).forEach(style => {
            style.animationConfigurations.forEach(animation => {
                const numberOfLevelObjects = animation.getNumberOfLevelObjectsForText(this.#textMetrics);
                animation.initializeValuesToAnimate(numberOfLevelObjects);
            });
            style.graphics.forEach(graphic => {
                graphic.animationConfigurations.forEach(animation => {
                    const numberOfLevelObjects = animation.getNumberOfLevelObjectsForGraphics(graphic);
                    animation.initializeValuesToAnimate(numberOfLevelObjects);
                });
            });
        });
    }

    override draw() {
        if (this.#textMetrics === null) {
            this._resetCanvas();
            return;
        }
        this.#spreadAnimatedValues();
        super.draw();

        this.context.save();
        this.#clipObject();
        this.#drawGraphics();
        this.#maskAndDrawText();
        this.#maskAndDrawHighlightText();
        this.context.restore();
    };

    #spreadAnimatedValues() {
        Object.values(this._styles).forEach(style => {
            style.animationConfigurations.forEach(animation => {
                animation.spreadAnimatedTextValuesToLevels(this.#textMetrics);
            });
            style.graphics.forEach(graphic => {
                graphic.animationConfigurations.forEach(animation => {
                    animation.spreadAnimatedGraphicValuesToLevels();
                });
            });
        });
    }

    /**
     * Apply any scale animation to the canvas context for the provided style.
     * Currently, scaling support is a bit limited:
     *  1. Scale on the normal style only supports object & line levels
     *  2. Scale on the highlight style only supports highlight level
     *  3. Object level scales is always scaled from the center
     *  4. Line and Highlight levels are always scaled from the top left
     */
    #applyScaleAnimation(style: CanvasTextStyleRenderer, level: DynamicLevel, metrics: CanvasTextMetricItemAPI) {
        let scaleX = DEFAULT_SCALE;
        let scaleY = DEFAULT_SCALE;
        let scaleAnchorXNormalized = 0.5;
        let scaleAnchorYNormalized = 0.5;
        if (level === DynamicLevel.line || level === DynamicLevel.highlight) {
            scaleAnchorXNormalized = 0;
            scaleAnchorYNormalized = 0;
        }
        const scaleAnchorX = scaleAnchorXNormalized * metrics.bounds.width + metrics.bounds.x;
        const scaleAnchorY = scaleAnchorYNormalized * metrics.bounds.height + metrics.bounds.y;
        style.animationConfigurations
            .filter(config => config.level === level && (config.property === 'scaleX' || config.property === 'scaleY'))
            .forEach(config => {
                switch (config.property) {
                    case 'scaleX': scaleX = config.computedValues[metrics.globalWordElementIndex]; break;
                    case 'scaleY': scaleY = config.computedValues[metrics.globalWordElementIndex]; break;
                }
            });
        if (scaleX === 1 && scaleY === 1) {
            return;
        }
        this.context.translate(scaleAnchorX, scaleAnchorY);
        this.context.scale(scaleX, scaleY);
        this.context.translate(-scaleAnchorX, -scaleAnchorY);
    }

    /**
     * Apply the object masks, since they mask everything, including object level graphics.
     */
    #clipObject() {
        const objectMasks = this.#filteredGraphics({ level: DynamicLevel.object, mask: true });
        objectMasks.forEach(mask => mask.clipElement(this.context, 0));
    }

    /**
     * Draw all the graphics in level order of object, line, word.
     */
    #drawGraphics() {
        // Draw the graphics in level order: in the editor (and therefore scene design) the graphics can be created in
        // any level order. We need to extract them and draw them in the correct order.
        const objectGraphics = this.#filteredGraphics({ level: DynamicLevel.object, mask: false });
        const lineGraphics = this.#filteredGraphics({ level: DynamicLevel.line, mask: false });
        const wordGraphics = this.#filteredGraphics({ level: DynamicLevel.word, mask: false });
        const highlightGraphics = this.#filteredGraphics({ level: DynamicLevel.highlight, mask: false });

        objectGraphics.forEach(graphic => graphic.draw(this.context));
        lineGraphics.forEach(graphic => graphic.draw(this.context));
        wordGraphics.forEach(graphic => graphic.draw(this.context));
        highlightGraphics.forEach(graphic => graphic.draw(this.context));
    };

    /**
     * Draw text, masking it appropriately at each of line and word levels.
     */
    #maskAndDrawText() {
        const lineMasks = this.#filteredGraphics({ level: DynamicLevel.line, mask: true });
        const wordMasks = this.#filteredGraphics({ level: DynamicLevel.word, mask: true });
        // Track the current word index, there is no 1:1 mapping from word masks to word metrics due to breaking spaces.
        let ixWord = 0;

        this.#applyScaleAnimation(this.normalStyle, DynamicLevel.object, this.#textMetrics);
        this.#textMetrics.lines.forEach((line, ixLine) => {
            this.context.save();
            this.#applyScaleAnimation(this.normalStyle, DynamicLevel.line, line);
            lineMasks.forEach(mask => mask.clipElement(this.context, ixLine));
            line.visibleWords.forEach((word) => {
                this.context.save();
                wordMasks.forEach(mask => mask.clipElement(this.context, ixWord));
                ixWord += 1;
                word.elements.forEach(element => {
                    element.draw(this.context);
                });
                this.context.restore();
            });
            this.context.restore();
        });
    };

    /**
     * Draw the highlight layer text
     */
    #maskAndDrawHighlightText() {
        const highlightMasks = this.#filteredGraphics({ level: DynamicLevel.highlight, mask: true });
        this.#textMetrics.highlightSpans.forEach((span, spanIndex) => {
            this.context.save();
            this.#applyScaleAnimation(this.highlightStyle, DynamicLevel.highlight, span);
            highlightMasks.forEach(mask => mask.clipElement(this.context, spanIndex));
            span.elements.forEach(element => {
                element.draw(this.context, true);
            });
            this.context.restore();
        });
    };

    #filteredGraphics({ level, mask }: { level: DynamicLevel; mask: boolean; }): CanvasTextDynamicGraphicRenderer[] {
        const style = level === DynamicLevel.highlight ? this.highlightStyle : this.normalStyle;
        return style.graphics.filter(graphic => graphic.level === level && graphic.asMask === mask);
    }

    override resetBuild() {
        this.#textMetrics = null;
    }

    buildAnimationManagerConfig(containerDuration: number): AnimationConfig[] {
        if (!this.#textMetrics) {
            return [];
        }
        const animationConfigs: AnimationConfig[] = [];
        Object.values(this._styles).forEach(style => {
            style.animationConfigurations.forEach(config => {
                const animationConfig = config.buildAnimationManagerConfig(
                    this.#getInferredValues.bind(this),
                    this.#getOffsetForProperty.bind(this),
                    containerDuration
                );
                if (animationConfig) {
                    animationConfigs.push(animationConfig);
                }
            });
            animationConfigs.push(...style.graphics.flatMap(
                graphic => graphic.buildAnimationManagerConfig(containerDuration)));
        });
        return animationConfigs;
    }


    #getInferredValues(inferredDimension: DynamicInferredDimensions, level: DynamicLevel): number[] {
        const metrics = this.#getLevelMetrics(level);
        switch (inferredDimension) {
            case DynamicInferredDimensions.height:
                return [metrics[0]?.height] ?? [0];
            case DynamicInferredDimensions.maxHeight:
                const maxHeight = Math.max(...metrics.map(metric => metric.height));
                return Array(metrics.length).fill(maxHeight);

            case DynamicInferredDimensions.width:
                return [metrics[0]?.width] ?? [0];
            case DynamicInferredDimensions.maxWidth:
                const maxWidth = Math.max(...metrics.map(metric => metric.width));
                return Array(metrics.length).fill(maxWidth);

            // These are unsupported for text
            case DynamicInferredDimensions.heightWithoutDescent:
            case DynamicInferredDimensions.gapless:
            case DynamicInferredDimensions.gaplessWidth:
            default:
                throw Error(`Unsupported inferred value ${inferredDimension}`);
        }
    }

    #getLevelMetrics(level: DynamicLevel): CanvasTextMetricItemAPI[] {
        switch (level) {
            case DynamicLevel.object:
                return [this.#textMetrics];
            case DynamicLevel.line:
                return this.#textMetrics.lines;
            case DynamicLevel.word:
                return this.#textMetrics.wordMetricsWithoutSpaces;
            case DynamicLevel.highlight:
                throw new Error(`Inferred values for the highlight level are not supported`);
            default:
                throw new Error(`Unsupported dynamic level ${level}`);
        }
    }


    /**
     * Applies an offset to the driven range to create the final driven range based on shouldOffsetPropertyValue.
     */
    #getOffsetForProperty(property: AnimatableTextProperties, shouldOffsetPropertyValue: boolean): number {
        let offset = 0;
        switch (property) {
            case 'x':
                if (!shouldOffsetPropertyValue) {
                    // position always gets offset in draw, so in the case of no offset we need to 'undo' the offset
                    offset = -this.translateX;
                }
                break;
            case 'y':
                if (!shouldOffsetPropertyValue) {
                    // position always gets offset in draw, so in the case of no offset we need to 'undo' the offset
                    offset = -this.translateY;
                }
                break;
            case 'alpha':
                if (shouldOffsetPropertyValue) {
                    // alpha is always assigned the computed value in draw, so if offsetting we offset it here
                    offset = DEFAULT_TEXT_ALPHA;
                }
                break;
            case 'scaleX':
            case 'scaleY':
                if (shouldOffsetPropertyValue) {
                    // scale is always assigned the computed value in draw, so if offsetting we offset it here
                    offset = DEFAULT_SCALE;
                }
                break;
            default:
                throw new Error(`Cannot apply offset to driven range for unsupported property "${property}"`);
        }
        return offset;
    }
}
